/**
 * NOS-61: React hook for custom domain verification flow.
 * Wraps domains edge function (add, verify, remove) with loading/error states.
 */
import { useState, useCallback, useEffect } from "react"
import { FunctionsHttpError } from "@supabase/supabase-js"
import { supabase } from "@/lib/supabase"
import { queuePublicCachePurge } from "@/lib/publicCache"

export type CustomDomainStatus =
  | "idle"
  | "pending"
  | "verified"
  | "failed"
  | null

export interface CustomDomainRecord {
  domain: string
  verification_status: "pending" | "verified" | "failed" | "expired"
  ssl_status: "pending" | "active" | "failed"
  dns_records: {
    cname?: { name: string; type: string; value: string }
    txt?: { name: string; type: string; value: string }
  } | null
  verified_at: string | null
}

export interface DnsInstructions {
  cname: { name: string; type: string; value: string }
  txt: { name: string; type: string; value: string }
}

async function extractErrorMessage(
  error: unknown,
  data: { error?: string } | null
): Promise<string> {
  if (data?.error && typeof data.error === "string") return data.error
  if (error instanceof FunctionsHttpError && error.context) {
    try {
      const res = error.context as Response
      const body = (await res.clone().json().catch(() => ({}))) as {
        error?: string
        details?: string[]
      }
      if (typeof body.error === "string") {
        if (Array.isArray(body.details) && body.details.length > 0) {
          return `${body.error}: ${body.details.join(" ")}`
        }
        return body.error
      }
    } catch {
      // ignore
    }
  }
  if (error instanceof Error) return error.message
  return "An unexpected error occurred"
}

export interface UseCustomDomainResult {
  /** Current domain record from custom_domains (or null if none) */
  domain: CustomDomainRecord | null
  /** Loading state for fetching domain status */
  isLoading: boolean
  /** Loading state for add/verify/remove operations */
  isBusy: boolean
  /** Error from last operation or fetch */
  error: string | null
  /** Add domain and return DNS instructions */
  addDomain: (
    domain: string,
    orgId: string
  ) => Promise<{ ok: boolean; dnsInstructions?: DnsInstructions; error?: string }>
  /** Verify domain (checks DNS, updates status, triggers SSL) */
  verifyDomain: (
    domain: string,
    orgId: string
  ) => Promise<{ ok: boolean; error?: string }>
  /** Remove domain from Vercel and custom_domains */
  removeDomain: (
    domain: string,
    orgId: string
  ) => Promise<{ ok: boolean; error?: string }>
  /** Refresh domain status from database */
  refreshDomain: (orgId: string) => Promise<void>
}

export function useCustomDomain(orgId: string | null): UseCustomDomainResult {
  const [domain, setDomain] = useState<CustomDomainRecord | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isBusy, setIsBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const refreshDomain = useCallback(async (org: string) => {
    if (!org) {
      setDomain(null)
      setIsLoading(false)
      return
    }
    setError(null)
    setIsLoading(true)
    const { data, error: fetchError } = await supabase
      .from("custom_domains")
      .select("domain, verification_status, ssl_status, dns_records, verified_at")
      .eq("org_id", org)
      .maybeSingle()

    setIsLoading(false)
    if (fetchError) {
      setError(fetchError.message)
      setDomain(null)
      return
    }
    setDomain(data as CustomDomainRecord | null)
  }, [])

  useEffect(() => {
    refreshDomain(orgId ?? "")
  }, [orgId, refreshDomain])

  const addDomain = useCallback(
    async (
      domainInput: string,
      org: string
    ): Promise<{
      ok: boolean
      dnsInstructions?: DnsInstructions
      error?: string
    }> => {
      setError(null)
      setIsBusy(true)
      const { data, error: fnError } = await supabase.functions.invoke(
        "domains",
        {
          body: { action: "add", domain: domainInput.trim().toLowerCase(), org_id: org },
        }
      )

      setIsBusy(false)
      if (fnError) {
        const msg = await extractErrorMessage(fnError, data)
        setError(msg)
        return { ok: false, error: msg }
      }

      const res = data as {
        success?: boolean
        dns_instructions?: DnsInstructions
        error?: string
      }
      if (res.success && res.dns_instructions) {
        await refreshDomain(org)
        return { ok: true, dnsInstructions: res.dns_instructions }
      }
      const errMsg = res.error ?? "Failed to add domain"
      setError(errMsg)
      return { ok: false, error: errMsg }
    },
    [refreshDomain]
  )

  const verifyDomain = useCallback(
    async (domainInput: string, org: string): Promise<{ ok: boolean; error?: string }> => {
      setError(null)
      setIsBusy(true)
      const { data, error: fnError } = await supabase.functions.invoke(
        "domains",
        {
          body: {
            action: "verify",
            domain: domainInput.trim().toLowerCase(),
            org_id: org,
          },
        }
      )

      setIsBusy(false)
      if (fnError) {
        const msg = await extractErrorMessage(fnError, data)
        setError(msg)
        return { ok: false, error: msg }
      }

      const res = data as { success?: boolean; verified?: boolean; error?: string }
      if (res.success && res.verified) {
        // Domain just went live — clear any cached responses so the new host
        // serves fresh content immediately.
        queuePublicCachePurge(org, { type: "domain-change" })
        await refreshDomain(org)
        return { ok: true }
      }
      const errMsg = res.error ?? "Verification failed"
      setError(errMsg)
      return { ok: false, error: errMsg }
    },
    [refreshDomain]
  )

  const removeDomain = useCallback(
    async (domainInput: string, org: string): Promise<{ ok: boolean; error?: string }> => {
      setError(null)
      setIsBusy(true)
      const { data, error: fnError } = await supabase.functions.invoke(
        "domains",
        {
          body: {
            action: "remove",
            domain: domainInput.trim().toLowerCase(),
            org_id: org,
          },
        }
      )

      setIsBusy(false)
      if (fnError) {
        const msg = await extractErrorMessage(fnError, data)
        setError(msg)
        return { ok: false, error: msg }
      }

      const res = data as { success?: boolean; error?: string }
      if (res.success) {
        queuePublicCachePurge(org, { type: "domain-change" })
        await refreshDomain(org)
        return { ok: true }
      }
      const errMsg = res.error ?? "Failed to remove domain"
      setError(errMsg)
      return { ok: false, error: errMsg }
    },
    [refreshDomain]
  )

  return {
    domain,
    isLoading,
    isBusy,
    error,
    addDomain,
    verifyDomain,
    removeDomain,
    refreshDomain,
  }
}
